import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog} from '../Dialog'
import {DialogHeader} from '../dialogs/AssignInvitationCodeSteps.tsx'
import {ActivationChannel} from '../../domain/entities/Activation.ts'
import {CRM_CHANNELS, HUMAN_OUTCOMES, LOST_REASONS, TeamMember} from '../../domain/entities/Pipeline.ts'
import {crmErrorMessage, dateInputToMillis, FIELD_CLASS, millisToDateInput, tomorrowDateInput} from './pipelineView.ts'
import {Spinner} from './PipelineParts.tsx'

// The backend accepts dates up to the end of the deadline day (contract §9.2).
const ONE_DAY_MS = 24 * 60 * 60 * 1000

export const Field: React.FC<{label: string; htmlFor: string; children: React.ReactNode; hint?: string}> = ({label, htmlFor, children, hint}) => (
    <div className="flex flex-col gap-1.5">
        <label htmlFor={htmlFor} className="text-[12.5px] font-medium text-neutralBlack-700">{label}</label>
        {children}
        {hint && <span className="text-[12px] text-neutralBlack-600">{hint}</span>}
    </div>
)

// A <dialog> (focus trap and Esc for free) opened while `isOpen` is true.
// `onSubmit` resolves to close it; it rejects to keep it open with the error.
export const FormDialog: React.FC<{
    isOpen: boolean
    title: string
    submitLabel: string
    canSubmit: boolean
    onClose: () => void
    onSubmit: () => Promise<unknown>
    children: React.ReactNode
}> = ({isOpen, title, submitLabel, canSubmit, onClose, onSubmit, children}) => {
    const {t} = useTranslation('dashboard')
    const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!isOpen) return
        setError(null)
        setBusy(false)
        dialog.current?.open().then(onClose)
        // onClose is stable enough for an open/close pair; reopening is driven by isOpen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    const close = () => dialog.current?.close()
    const submit = () => {
        if (!canSubmit || busy) return
        setBusy(true)
        setError(null)
        onSubmit().then(close).catch((e) => setError(crmErrorMessage(e, t))).finally(() => setBusy(false))
    }

    return (
        <Dialog ref={dialog} customWidth="480px" childrenPadding="p-0"
                panelClassName="rounded-[22px] backdrop:bg-white/60 backdrop:backdrop-blur-md">
            <div className="pb-4">
                <DialogHeader title={title} onClose={close}/>
            </div>
            <form className="px-[26px] pb-2 flex flex-col gap-3.5" onSubmit={(e) => {e.preventDefault(); submit()}}>
                {children}
                {error && <p role="alert" className="text-[12.5px] leading-[1.5] text-[#B42318]">{error}</p>}
                <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true"/>
            </form>
            <div className="mt-4 flex items-center justify-end gap-3 border-t border-neutralBlack-100 bg-neutralBlack-50 px-[26px] py-4 rounded-b-[22px]">
                <button type="button" onClick={close}
                        className="h-10 px-4 rounded-full border-[1.5px] border-neutralBlack-300 text-[13.5px] font-semibold text-neutralBlack-900 hover:border-neutralBlack-600 transition-colors duration-200 ease-out">
                    {t('cancel')}
                </button>
                <button type="button" onClick={submit} disabled={!canSubmit || busy}
                        className="h-10 px-4 min-w-[96px] inline-flex items-center justify-center rounded-full bg-neutralBlack-900 text-white text-[13.5px] font-semibold hover:bg-neutralBlack-700 disabled:bg-neutralBlack-100 disabled:text-neutralBlack-600 disabled:cursor-not-allowed transition-colors duration-200 ease-out">
                    {busy ? <Spinner/> : submitLabel}
                </button>
            </div>
        </Dialog>
    )
}

// Mark as lost, from the panel footer, the card menu or a drop on a Lost column.
// Reasons are per pipeline (Activation: LostReason; Conversion: ConversionLostReason).
export const MarkLostDialog: React.FC<{
    request: {reason: string | null} | null
    allowedReasons?: string[]
    onClose: () => void
    onSubmit: (reason: string, note: string) => Promise<unknown>
}> = ({request, allowedReasons = LOST_REASONS, onClose, onSubmit}) => {
    const {t} = useTranslation('dashboard')
    const [reason, setReason] = useState<string>('')
    const [note, setNote] = useState('')
    useEffect(() => {
        if (!request) return
        setReason(request.reason ?? '')
        setNote('')
    }, [request])

    return (
        <FormDialog isOpen={!!request} title={t('pipelines.lost.title')} submitLabel={t('pipelines.lost.submit')}
                    canSubmit={!!reason} onClose={onClose} onSubmit={() => onSubmit(reason, note.trim())}>
            <fieldset className="flex flex-col gap-2">
                <legend className="text-[12.5px] font-medium text-neutralBlack-700 mb-1.5">{t('pipelines.lost.reason')}</legend>
                {allowedReasons.map((r) => (
                    <label key={r} className="flex items-center gap-2.5 text-[13.5px] text-neutralBlack-900 cursor-pointer">
                        <input type="radio" name="lost-reason" value={r} checked={reason === r} onChange={() => setReason(r)}
                               className="accent-neutralBlack-900"/>
                        {t(`pipelines.lostReason.${r}`)}
                    </label>
                ))}
            </fieldset>
            {reason === 'OPTED_OUT' && <p className="text-[12.5px] text-[#8A5A00]">{t('pipelines.lost.optedOutHint')}</p>}
            <Field label={t('pipelines.form.noteOptional')} htmlFor="lost-note">
                <textarea id="lost-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                          className={`${FIELD_CLASS} h-auto py-2 resize-none`}/>
            </Field>
        </FormDialog>
    )
}

// Mark as done with an outcome (TOUCHPOINT_DONE).
// Outcomes are per pipeline (Activation: HumanOutcome; Conversion: ConversionOutcome).
export const MarkDoneDialog: React.FC<{
    isOpen: boolean
    defaultChannel: ActivationChannel
    outcomes?: string[]
    deadline: number | null
    // Where each outcome's help text lives (Conversion outcomes behave differently).
    helpKeyPrefix?: string
    onClose: () => void
    onSubmit: (data: {outcome: string; channel: ActivationChannel; date?: number; note: string}) => Promise<unknown>
}> = ({isOpen, defaultChannel, outcomes, deadline, helpKeyPrefix = 'pipelines.outcomeHelp', onClose, onSubmit}) => {
    const {t} = useTranslation('dashboard')
    const [outcome, setOutcome] = useState<string>('')
    const [channel, setChannel] = useState<ActivationChannel>(defaultChannel)
    const [date, setDate] = useState('')
    const [note, setNote] = useState('')
    useEffect(() => {
        if (!isOpen) return
        setOutcome('')
        setChannel(defaultChannel)
        setDate('')
        setNote('')
    }, [isOpen, defaultChannel])

    const dateMillis = dateInputToMillis(date)
    const needsDate = outcome === 'WILL_DO_IT'
    const dateValid = !needsDate || (dateMillis != null && dateMillis > Date.now() && (deadline == null || dateMillis < deadline + ONE_DAY_MS))
    const list: string[] = outcomes?.length ? outcomes : HUMAN_OUTCOMES

    return (
        <FormDialog isOpen={isOpen} title={t('pipelines.done.title')} submitLabel={t('pipelines.done.submit')}
                    canSubmit={!!outcome && dateValid} onClose={onClose}
                    onSubmit={() => onSubmit({outcome, channel, date: needsDate ? dateMillis ?? undefined : undefined, note: note.trim()})}>
            <fieldset className="flex flex-col gap-2">
                <legend className="text-[12.5px] font-medium text-neutralBlack-700 mb-1.5">{t('pipelines.done.outcome')}</legend>
                {list.map((o) => (
                    <label key={o} className="flex items-start gap-2.5 cursor-pointer">
                        <input type="radio" name="done-outcome" value={o} checked={outcome === o} onChange={() => setOutcome(o)}
                               className="mt-[3px] accent-neutralBlack-900"/>
                        <span className="flex flex-col">
                            <span className="text-[13.5px] text-neutralBlack-900">{t(`pipelines.outcome.${o}`)}</span>
                            <span className="text-[12px] text-neutralBlack-600">{t(`${helpKeyPrefix}.${o}`)}</span>
                        </span>
                    </label>
                ))}
            </fieldset>
            {needsDate && (
                <Field label={t('pipelines.done.date')} htmlFor="done-date" hint={t('pipelines.done.dateHint')}>
                    <input id="done-date" type="date" value={date} min={tomorrowDateInput()}
                           max={deadline != null ? millisToDateInput(deadline) : undefined}
                           onChange={(e) => setDate(e.target.value)} className={FIELD_CLASS}/>
                </Field>
            )}
            <Field label={t('pipelines.form.channel')} htmlFor="done-channel">
                <select id="done-channel" value={channel} onChange={(e) => setChannel(e.target.value as ActivationChannel)} className={FIELD_CLASS}>
                    {CRM_CHANNELS.map((c) => <option key={c} value={c}>{t(`pipelines.channel.${c}`)}</option>)}
                </select>
            </Field>
            <Field label={t('pipelines.form.noteOptional')} htmlFor="done-note">
                <textarea id="done-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                          className={`${FIELD_CLASS} h-auto py-2 resize-none`}/>
            </Field>
        </FormDialog>
    )
}

// Add follow-up (ADD_FOLLOW_UP): a human touch on a date, in the current stage.
export const AddFollowUpDialog: React.FC<{
    isOpen: boolean
    defaultChannel: ActivationChannel
    team: TeamMember[]
    currentUserId: string | null
    deadline: number | null
    onClose: () => void
    onSubmit: (data: {date: number; channel: ActivationChannel; assignee?: string; note: string}) => Promise<unknown>
}> = ({isOpen, defaultChannel, team, currentUserId, deadline, onClose, onSubmit}) => {
    const {t} = useTranslation('dashboard')
    const [date, setDate] = useState('')
    const [channel, setChannel] = useState<ActivationChannel>(defaultChannel)
    const [assignee, setAssignee] = useState('')
    const [note, setNote] = useState('')
    useEffect(() => {
        if (!isOpen) return
        setDate(tomorrowDateInput())
        setChannel(defaultChannel)
        setAssignee(currentUserId && team.some((m) => m.id === currentUserId) ? currentUserId : '')
        setNote('')
    }, [isOpen, defaultChannel, currentUserId, team])

    const dateMillis = dateInputToMillis(date)
    const dateValid = dateMillis != null && dateMillis > Date.now() && (deadline == null || dateMillis < deadline + ONE_DAY_MS)

    return (
        <FormDialog isOpen={isOpen} title={t('pipelines.followUp.title')} submitLabel={t('pipelines.followUp.submit')}
                    canSubmit={dateValid} onClose={onClose}
                    onSubmit={() => onSubmit({date: dateMillis!, channel, assignee: assignee || undefined, note: note.trim()})}>
            <Field label={t('pipelines.followUp.date')} htmlFor="fu-date">
                <input id="fu-date" type="date" value={date} min={tomorrowDateInput()}
                       max={deadline != null ? millisToDateInput(deadline) : undefined}
                       onChange={(e) => setDate(e.target.value)} className={FIELD_CLASS}/>
            </Field>
            <Field label={t('pipelines.form.channel')} htmlFor="fu-channel">
                <select id="fu-channel" value={channel} onChange={(e) => setChannel(e.target.value as ActivationChannel)} className={FIELD_CLASS}>
                    {CRM_CHANNELS.map((c) => <option key={c} value={c}>{t(`pipelines.channel.${c}`)}</option>)}
                </select>
            </Field>
            {team.length > 0 && (
                <Field label={t('pipelines.followUp.assignee')} htmlFor="fu-assignee">
                    <select id="fu-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} className={FIELD_CLASS}>
                        <option value="">{t('pipelines.followUp.assigneeMe')}</option>
                        {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                </Field>
            )}
            <Field label={t('pipelines.form.noteOptional')} htmlFor="fu-note">
                <textarea id="fu-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                          className={`${FIELD_CLASS} h-auto py-2 resize-none`}/>
            </Field>
        </FormDialog>
    )
}
